import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICreateOperationRequest,
  IOperationRequestResponse,
} from 'types/api/operationsAimApi.interface'
import { IOpsRequest, IOpsRequestsSlice, OpsRequestPriority } from 'types/opsRequests.interface'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import {
  deptHrIdByKey,
  mapRequest,
  neededByTimeToIso,
} from 'pages/Operations/Shared/opsApiMappers'
import { REQUEST_PRIORITY_TO_API } from 'pages/Operations/Shared/opsEnumMaps'
import { OPS_NOW } from 'pages/Operations/Shared/opsRequestsData'
const initialState: IOpsRequestsSlice = {
  isLoading: false,
  requests: [],
}

export interface IRaiseOpsRequestPayload {
  fromDeptKey: string
  toDeptKey: string
  title: string
  description: string
  priority: OpsRequestPriority
  neededByTime: string
  profiles: IOpsDepartment[]
}

const buildCreateBody = (payload: IRaiseOpsRequestPayload): ICreateOperationRequest => {
  const fromDeptId = deptHrIdByKey(payload.profiles, payload.fromDeptKey)
  const toDeptId = deptHrIdByKey(payload.profiles, payload.toDeptKey)
  if (!fromDeptId || !toDeptId) throw new Error('Department not found')
  return {
    fromDeptId,
    toDeptId,
    title: payload.title,
    description: payload.description,
    priority: REQUEST_PRIORITY_TO_API[payload.priority],
    neededBy: neededByTimeToIso(payload.neededByTime),
  }
}

export const getOpsRequests = createAsyncThunk(
  'opsRequests/getOpsRequests',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationRequestResponse[]> = await baseUrl.get(
        'aim-operations/request',
        { params },
      )
      const profiles = (thunkAPI.getState() as { deptOpsProfiles: { profiles: IOpsDepartment[] } }).deptOpsProfiles
        .profiles
      return (resp.data || []).map((r) => mapRequest(r, profiles))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const raiseOpsRequest = createAsyncThunk(
  'opsRequests/raiseOpsRequest',
  async (
    { payload, params }: { payload: IRaiseOpsRequestPayload; params?: any },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IOperationRequestResponse> = await baseUrl.post(
        'aim-operations/request',
        buildCreateBody(payload),
      )
      if (params) await thunkAPI.dispatch(getOpsRequests(params))
      toast.success('Request raised')
      const profiles = (thunkAPI.getState() as { deptOpsProfiles: { profiles: IOpsDepartment[] } }).deptOpsProfiles
        .profiles
      return mapRequest(resp.data, profiles)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const postLifecycle = (path: string) =>
  createAsyncThunk(
    `opsRequests/${path}`,
    async ({ id, params }: { id: number; params?: any }, thunkAPI) => {
      try {
        const resp: AxiosResponse<IOperationRequestResponse> = await baseUrl.post(
          `aim-operations/request/${id}/${path}`,
        )
        if (params) await thunkAPI.dispatch(getOpsRequests(params))
        const profiles = (thunkAPI.getState() as { deptOpsProfiles: { profiles: IOpsDepartment[] } }).deptOpsProfiles
        .profiles
        return mapRequest(resp.data, profiles)
      } catch (error) {
        return thunkAPI.rejectWithValue(error)
      }
    },
  )

export const acknowledgeOpsRequest = postLifecycle('acknowledge')
export const startOpsRequest = postLifecycle('start')
export const completeOpsRequest = postLifecycle('complete')

export const declineOpsRequest = createAsyncThunk(
  'opsRequests/declineOpsRequest',
  async (
    { id, reason, params }: { id: number; reason: string; params?: any },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IOperationRequestResponse> = await baseUrl.post(
        `aim-operations/request/${id}/decline`,
        { reason },
      )
      if (params) await thunkAPI.dispatch(getOpsRequests(params))
      const profiles = (thunkAPI.getState() as { deptOpsProfiles: { profiles: IOpsDepartment[] } }).deptOpsProfiles
        .profiles
      return mapRequest(resp.data, profiles)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const opsRequestsSlice = createSlice({
  name: 'opsRequests',
  initialState,
  reducers: {
    clearOpsRequests: (state) => {
      state.requests = []
    },
    raiseRequest: (
      state,
      action: PayloadAction<Omit<IOpsRequest, 'id' | 'code' | 'status' | 'declineReason' | 'acknowledgedAt' | 'escalationLevel' | 'createdAt'>>,
    ) => {
      const nextId = state.requests.reduce((m, r) => Math.max(m, r.id), 0) + 1
      state.requests = [
        {
          ...action.payload,
          id: nextId,
          code: `REQ-${String(nextId).padStart(3, '0')}`,
          status: 'open',
          declineReason: null,
          acknowledgedAt: null,
          escalationLevel: 0,
          createdAt: OPS_NOW,
        },
        ...state.requests,
      ]
    },
    acknowledgeRequest: (state, action: PayloadAction<number>) => {
      state.requests = state.requests.map((r) =>
        r.id === action.payload ? { ...r, status: 'acknowledged', acknowledgedAt: OPS_NOW } : r,
      )
    },
    startRequest: (state, action: PayloadAction<number>) => {
      state.requests = state.requests.map((r) => (r.id === action.payload ? { ...r, status: 'in_progress' } : r))
    },
    completeRequest: (state, action: PayloadAction<number>) => {
      state.requests = state.requests.map((r) => (r.id === action.payload ? { ...r, status: 'done' } : r))
    },
    declineRequest: (state, action: PayloadAction<{ id: number; reason: string }>) => {
      state.requests = state.requests.map((r) =>
        r.id === action.payload.id ? { ...r, status: 'declined', declineReason: action.payload.reason } : r,
      )
    },
  },
  extraReducers: {
    [getOpsRequests.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOpsRequests.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.requests = payload
    },
    [getOpsRequests.rejected.type]: (state, action) => {
      state.isLoading = false
      handleErrors(action.payload)
    },
    [raiseOpsRequest.fulfilled.type]: (state, { payload }) => {
      if (payload) state.requests = [payload, ...state.requests.filter((r) => r.id !== payload.id)]
    },
    [raiseOpsRequest.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [acknowledgeOpsRequest.rejected.type]: (state, action) => handleErrors(action.payload),
    [startOpsRequest.rejected.type]: (state, action) => handleErrors(action.payload),
    [completeOpsRequest.rejected.type]: (state, action) => handleErrors(action.payload),
    [declineOpsRequest.rejected.type]: (state, action) => handleErrors(action.payload),
  },
})

export const {
  clearOpsRequests,
  raiseRequest,
  acknowledgeRequest,
  startRequest,
  completeRequest,
  declineRequest,
} = opsRequestsSlice.actions
export default opsRequestsSlice.reducer
